"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface StatCounterProps {
  end: number;
  suffix?: string;
  isVisible: boolean;
}
function StatCounter({
  end,
  suffix = "",
  isVisible
}: StatCounterProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    const duration = 2500;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * end));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };
    window.requestAnimationFrame(step);
  }, [end, isVisible]);
  return <span>
      {count.toLocaleString()}
      {suffix}
    </span>;
}
const metricsData = [{
  label: "Capital Managed",
  value: 500,
  suffix: "M+"
}, {
  label: "Market Share",
  value: 24,
  suffix: "%"
}, {
  label: "Global Campaigns",
  value: 1250,
  suffix: ""
}, {
  label: "ROI Multiplier",
  value: 12,
  suffix: "x"
}];
export default function ModernLearningMetrics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="224325" id="global-impact-in-numbers" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661389/img/market_indices_ticker_dark.png)] bg-cover bg-center opacity-5 mix-blend-screen pointer-events-none" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {metricsData.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col items-center justify-center text-center">
              {}
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-primary opacity-20 blur-[12px] animate-pulse" />
                <div className="relative size-2.5 rounded-sm bg-primary shadow-[0_0_15px_var(--color-primary)]" />
              </div>

              {}
              <div className="text-5xl md:text-6xl lg:text-7xl font-black font-default text-foreground tracking-tighter mb-3">
                <StatCounter end={metric.value} suffix={metric.suffix} isVisible={isVisible} />
              </div>

              {}
              <div className="text-xs md:text-sm font-medium font-mono text-muted-foreground uppercase tracking-[0.3em]">
                {metric.label}
              </div>

              {}
              <div className="w-full h-px bg-border mt-6 relative overflow-hidden">
                <div className={`absolute inset-y-0 left-0 bg-primary transition-all duration-[2500ms] ease-out ${isVisible ? 'w-full' : 'w-0'}`} />
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}